import api from '@/api'

interface ResponseWrapper<TResult> {
    result: TResult
    code: string
    message: string
    error: string | null
}

export default {
    createEvent(data: ewsResource.api.DynamicApiOptions): Promise<string> {
        return api.post({
          url: '/Mail/Calendar/api/create?funcName=createEvent',
          data: data,
        });
    },

    editEvent(data: ewsResource.api.DynamicApiOptions): Promise<any> {
        return api.post({
          url: '/Mail/Calendar/api/set?funcName=editEvent',
          data: data,
        });
    },

    cancelEvent(id: string) {
        return api.post<any>({
          url: '/Mail/Calendar/api/cancel?funcName=cancelEvent',
          data: {
            id: id,
          },
        });
    },    

    getContent(id: string, resource: string): Promise<string> {
        return api.get<string>({
            url: `/Portal/ReservationEws/api/getContent?itemId=${encodeURIComponent(id)}&resource=${resource}`,
        })
    },

    getOriginalAppointment(iCalUid: string, start?: string, end?: string): Promise<any> {
        return api.post({
            url: '/Portal/ReservationEws/api/getOriginalAppointment',
            data: {
                contract: {
                    fields: [
                        { key: "Id" },
                        { key: "ICalUid" },                    
                        { key: "ParentFolderId" },
                        { key: "AllowedResponseActions" },
                        { key: "EVENT_STATUS", as: 'status' },
                        { key: "IS_BIG_FILE", as: 'hasBigFiles' },
                        { key: "Subject" },
                        { key: "DisplayTo" },
                        { key: "TimeZone" },
                        { key: "Sensitivity" },
                        { key: "Importance" },
                        { key: "ReminderDueBy" },
                        { key: "Location" },
                        { key: "LegacyFreeBusyStatus" },
                        { key: "Organizer", format: "obj:[name,address]" },
                        // { key: "hasAttachments" },
                        { key: "MyResponseType" },
                        { key: "IsRecurring" },
                        { key: "IsFromMe" },
                        { key: "IsCancelled" },
                        { key: "IsAllDayEvent" },
                        { key: "Start", "format": "yyyy-MM-dd HH:mm:ss" },
                        { key: "End", "format": "yyyy-MM-dd HH:mm:ss" },
                        { key: "Duration", "as": "ScheduleDuration" },
                        { key: "DateTimeCreated", "format": "yyyy-MM-dd" },
                        { key: "RequiredAttendees", format: "obj:[name,address]", as: "requiredAttendees" },
                        { key: "OptionalAttendees", format: "obj:[name,address]", as: "optionalAttendees" },
                        { key: "Categories" },
                        { key: "PidLidRecurrenceType" },
                        { key: "PidLidRecurrencePattern" },
                        { key: "PidLidRecurring" },
                        { key: "Recurrence" },
                        { key: "ReminderMinutesBeforeStart"},
                        { key: "Resources", format: "obj:[name,address]" },
                        { key: "AppointmentType" },
                        {
                            key: 'Attachments',
                            format: 'obj:[id,name,contentType,size]',
                            filters: [
                                {
                                    key: 'isInline',
                                    value: false,
                                    operator: 'eq',
                                },
                            ],
                        },
                    ],
                    itemId: iCalUid,
                    type: "Calendar"
                },
                occurrenceStart: start,
                occurrenceEnd: end
            }
        });
    },

    getRecurringMasterID(id: string) {
        return api.post<string>({
          url: '/Mail/Calendar/api/getmasterid?funcName=getRecurringMasterID',
          data: {
            id: id,
          },
        });
    },

    getDetail(id: string, resource: string): Promise<reserve.CalendarItem> {
        return api.post({
            url: `/Portal/ReservationEws/api/getDetail?resource=${resource}`,
            data: {
                fields: [
                    { key: "Id" },
                    { key: "ICalUid" },                    
                    { key: "ParentFolderId" },
                    { key: "AllowedResponseActions" },
                    { key: "EVENT_STATUS", as: 'status' },
                    { key: "IS_BIG_FILE", as: 'hasBigFiles' },
                    { key: "Subject" },
                    { key: "DisplayTo" },
                    { key: "TimeZone" },
                    { key: "Sensitivity" },
                    { key: "Importance" },
                    { key: "ReminderDueBy" },
                    { key: "Location" },
                    { key: "LegacyFreeBusyStatus" },
                    { key: "Organizer", format: "obj:[name,address]" },
                    // { key: "hasAttachments" },
                    { key: "MyResponseType" },
                    { key: "IsRecurring" },
                    { key: "IsFromMe" },
                    { key: "IsCancelled" },
                    { key: "IsAllDayEvent" },
                    { key: "Start", "format": "yyyy-MM-dd HH:mm:ss" },
                    { key: "End", "format": "yyyy-MM-dd HH:mm:ss" },
                    { key: "Duration", "as": "ScheduleDuration" },
                    { key: "DateTimeCreated", "format": "yyyy-MM-dd" },
                    { key: "RequiredAttendees", format: "obj:[name,address]", as: "requiredAttendees" },
                    { key: "OptionalAttendees", format: "obj:[name,address]", as: "optionalAttendees" },
                    { key: "Categories" },
                    { key: "PidLidRecurrenceType" },
                    { key: "PidLidRecurrencePattern" },
                    { key: "PidLidRecurring" },
                    { key: "Recurrence" },
                    { key: "ReminderMinutesBeforeStart"},
                    { key: "Resources", format: "obj:[name,address]" },
                    { key: "AppointmentType" },
                    {
                        key: 'Attachments',
                        format: 'obj:[id,name,contentType,size]',
                        filters: [
                            {
                                key: 'isInline',
                                value: false,
                                operator: 'eq',
                            },
                        ],
                    },
                ],
                itemId: id,
                type: "Calendar",
            }
        }).then((res: any) => {
            return res.item;
        })
    },

    getSubject(id: string): Promise<any> {
        return api.post<any>({
            url: '/Mail/Dynamic/api/get',
            data: {
                fields: [
                    { key: "Id" },
                    { key: "Subject" },
                ],
                itemId: id,
                type: "EmailMessage",
            }
        })
    },

    getAttendeesList (id: string, resource: string): Promise<any> {
        return api.post({
            url: `/Portal/ReservationEws/api/getDetail?resource=${resource}`,
            data: {
                fields: [
                    { key: "Organizer", format: "obj:[name,address]" },
                    { key: "RequiredAttendees", format: "obj:[name,address]", as: "requiredAttendees" },
                    { key: "OptionalAttendees", format: "obj:[name,address]", as: "optionalAttendees" },
                ],
                itemId: id,
                type: "Calendar",
            }
        })
    },
}